import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { History, ArrowRight, Users, ShoppingCart, DollarSign } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ownerLabel = (mgmt, repName) => {
  if (mgmt === 'SALES_REP') return repName ? `${repName}` : 'Sales Rep';
  return 'TC Direct';
};

// Compact ownership reassignment timeline shown inside the admin customer edit dialog.
export const OwnershipHistory = ({ customerId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/customers/${customerId}/ownership-history`);
        if (active) setHistory(res.data.history || []);
      } catch (e) {
        if (active) setHistory([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [customerId]);

  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-4 space-y-3" data-testid="ownership-history-section">
      <div className="flex items-center gap-2">
        <History className="w-4 h-4 text-[#C6A15B]" />
        <h4 className="text-sm font-semibold text-[#E7CB85]">Account Ownership History</h4>
      </div>
      {loading ? (
        <p className="text-xs text-slate-500">Loading history…</p>
      ) : history.length === 0 ? (
        <p className="text-xs text-slate-500">No ownership changes recorded yet.</p>
      ) : (
        <ol className="space-y-2.5">
          {history.map((h) => (
            <li key={h.audit_id} className="relative pl-4 border-l border-white/10" data-testid={`ownership-history-item-${h.audit_id}`}>
              <span className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-[#C6A15B]" />
              <div className="flex items-center gap-2 text-sm text-slate-200 flex-wrap">
                <span className="text-slate-400">{ownerLabel(h.previous_management_type, h.previous_salesperson_name)}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-white font-medium">{ownerLabel(h.new_management_type, h.new_salesperson_name)}</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {h.changed_at ? new Date(h.changed_at).toLocaleString() : '—'}
                {h.changed_by_name ? ` · by ${h.changed_by_name}` : ''}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// Lightweight read-only rollup shown when editing a sales_rep user (admin visibility).
export const SalesRepStatsStrip = ({ userId }) => {
  const [stat, setStat] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/sales-reps/stats`);
        const found = (res.data.sales_reps || []).find((r) => r.user_id === userId) || null;
        if (active) setStat(found);
      } catch (e) {
        if (active) setStat(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [userId]);

  if (loading) return <div className="animate-pulse bg-white/[0.05] rounded-lg h-16" />;
  const s = stat || { assigned_clients: 0, orders: 0, sales: 0 };
  const tiles = [
    { icon: Users, label: 'Clients', value: s.assigned_clients, accent: 'text-emerald-400' },
    { icon: ShoppingCart, label: 'Orders', value: s.orders, accent: 'text-blue-400' },
    { icon: DollarSign, label: 'Sales', value: money(s.sales), accent: 'text-[#C6A15B]' },
  ];
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-4" data-testid="sales-rep-stats-strip">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Sales Representative Activity</p>
      <div className="grid grid-cols-3 gap-3">
        {tiles.map((t) => (
          <div key={t.label}>
            <div className="flex items-center gap-1.5 mb-1"><t.icon className={`w-3.5 h-3.5 ${t.accent}`} /><span className="text-[11px] text-slate-400">{t.label}</span></div>
            <p className="text-white font-semibold text-sm">{t.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OwnershipHistory;
